/**
 * The public and claim surfaces' shell, from `kaynak/screens.html:196` and `:207`.
 *
 * Both are `section.public` with a `.public-top` banner, a `.public-main` and a `.public-foot`.
 * They differ in exactly three places, so they are one component with three inputs rather than
 * two near-identical copies: the brand's sub-line, whether the footer is fixed text or rendered
 * per record, and the `id` the parity harness's route proof looks for.
 *
 * The language switch is the reference's own `.lang` group — two buttons, never a select — and it
 * is part of the shell because both surfaces carry it above their content.
 */
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type PublicLang = 'tr' | 'en';

interface PublicShellProps {
  /** `publicScreen` or `claimScreen`; the harness proves a capture by this. */
  id: 'publicScreen' | 'claimScreen';
  /** The brand block's second line. */
  brandSub: string;
  lang: PublicLang;
  onLang: (lang: PublicLang) => void;
  children: ReactNode;
  /** The claim surface's footer is fixed text; the public one is rendered per record. */
  footer: ReactNode;
}

export function PublicShell({
  id,
  brandSub,
  lang,
  onLang,
  children,
  footer,
}: PublicShellProps): ReactElement {
  return (
    /*
     * `lang` is what makes an uppercase label legible.
     *
     * `.cred-dl dt`, `.eyebrow`, `.brand .sub` and the rest are uppercased by CSS, and CSS folds
     * case by the ELEMENT'S language. The document declares `tr`, so the English public page's
     * «Credential ID» came out «CREDENTİAL ID» — Turkish's dotted capital, in an English label.
     * Declaring the surface's own language fixes every such label at once, which is why it is done
     * here and not on the one heading that was reported.
     */
    <section id={id} className="public" lang={lang}>
      <header className="public-top">
        <Link className="brand" to="/verify">
          <span className="mark" />
          <span className="txt">
            <b>Happy Place to Work</b>
            <span className="sub" id={id === 'publicScreen' ? 'pubBrandSub' : undefined}>
              {brandSub}
            </span>
          </span>
        </Link>
        {/* The reference uses a div with role="group" here. A <fieldset> would carry the UA's
            own border, margin and legend box, and `.lang` styles a plain inline-flex pill — so the
            swap the linter suggests would move pixels on every public and claim page. */}
        {/* biome-ignore lint/a11y/useSemanticElements: the reference's own element; a fieldset changes the box */}
        <div className="lang" role="group" aria-label="Dil">
          <button
            type="button"
            data-lang="tr"
            className={lang === 'tr' ? 'is-on' : ''}
            onClick={() => onLang('tr')}
          >
            TR
          </button>
          <button
            type="button"
            data-lang="en"
            className={lang === 'en' ? 'is-on' : ''}
            onClick={() => onLang('en')}
          >
            EN
          </button>
        </div>
      </header>
      <main className="public-main" id={id === 'publicScreen' ? 'publicMain' : 'claimMain'}>
        {children}
      </main>
      <footer className="public-foot" id={id === 'publicScreen' ? 'publicFoot' : undefined}>
        {footer}
      </footer>
    </section>
  );
}

/** The claim surface's footer, fixed by the reference. */
export const CLAIM_FOOTER =
  'Happy Place to Work · Bu bağlantı yalnız size gönderildi; onaylamadan hiçbir bilginiz yayınlanmaz.';
